import { z } from 'zod';
import { compararQuantidade, somarQuantidade } from './aritmetica.ts';
import { dataDoFatoSchema, quantidadeSchema, uuidSchema } from './comum.ts';
import {
  chaveDoItemDeConsumo,
  lancamentoConsumoSchema,
  type CriarLancamentoConsumo,
} from './lancamentos-consumo.ts';

/**
 * Distribuição de consumo — o material que saiu do depósito **sem destino
 * certo**, repartido depois entre as obras e as etapas delas.
 *
 * O lançamento de consumo (`lancamentos-consumo.ts`) parte da obra: quem desce ao
 * almoxarifado já sabe para qual canteiro leva o carrinho, e a requisição fixa a
 * obra no cabeçalho. Nem sempre é assim. O cimento do almoxarifado central
 * abastece três obras na mesma semana, e só no fim dela o engenheiro sabe quanto
 * foi para a fundação da Aurora e quanto para a laje do Bela Vista. A
 * distribuição parte do **depósito**: escolhe-se o material, vê-se o saldo, e
 * reparte-se a quantidade entre obra, etapa e quantidade.
 *
 * ## Não é documento novo
 *
 * A gravação produz **lançamentos de consumo comuns**, um por obra — decisão do
 * Adriano em 25/09/2026. A alternativa, um documento próprio de distribuição,
 * exigiria uma quinta origem de movimentação de estoque, outra numeração, outra
 * exclusão e o ajuste de cada relatório que já soma consumo por obra (painel de
 * obras, custo por etapa, cardex). Gerando consumos, tudo isso continua certo sem
 * ser tocado: a distribuição é um jeito novo de **lançar**, e não uma coisa nova
 * a lançar.
 *
 * **Um por obra**, porque a obra é do cabeçalho do consumo. As linhas da mesma
 * obra — materiais diferentes, ou o mesmo material em etapas diferentes — caem no
 * mesmo documento. O mesmo material na mesma obra e na mesma etapa duas vezes é a
 * linha digitada duas vezes, e é recusado.
 *
 * **Tudo numa transação.** Se a requisição da terceira obra for recusada, as duas
 * primeiras não ficam: uma distribuição pela metade deixaria o saldo do depósito
 * num estado que ninguém escolheu, e a tela não teria como dizer o que gravou e
 * o que não.
 */

/** Teto de linhas numa distribuição — o mesmo do consumo, pelo mesmo motivo. */
export const MAXIMO_LINHAS_DISTRIBUICAO = 200;

const quantidadeDistribuidaSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade distribuída deve ser maior que zero.',
);

/**
 * Uma linha: quanto de um material vai para uma obra (e, opcionalmente, uma
 * etapa dela).
 *
 * Sem valor, pela mesma razão do item de consumo: a saída consome o custo médio
 * vigente, e quem o aplica é o servidor.
 */
export const linhaDistribuicaoConsumoSchema = z.object({
  insumoId: uuidSchema,
  obraId: uuidSchema,
  /** Opcional, e `null` quando não escolhida — nem toda obra tem etapas. */
  obraParteId: uuidSchema.nullable().prefault(null),
  quantidade: quantidadeDistribuidaSchema,
  observacao: z
    .string()
    .trim()
    .max(500, 'A observação da linha tem no máximo 500 caracteres.')
    .prefault(''),
});

export type LinhaDistribuicaoConsumo = z.infer<typeof linhaDistribuicaoConsumoSchema>;

/** A chave que não se repete numa distribuição: obra + material + etapa. */
export function chaveDaLinhaDeDistribuicao(linha: {
  obraId: string;
  insumoId: string;
  obraParteId?: string | null | undefined;
}): string {
  return `${linha.obraId}|${chaveDoItemDeConsumo(linha)}`;
}

export const MENSAGEM_LINHA_DE_DISTRIBUICAO_REPETIDA =
  'Este material já vai para esta obra e esta etapa em outra linha. Some as quantidades numa linha só.';

/**
 * O payload da distribuição.
 *
 * O cabeçalho é o que os consumos gerados terão em comum: empresa, depósito,
 * data, tipo de movimentação e observação. A obra **não** está aqui — ela é de
 * cada linha, e é o que decide quantos documentos nascem.
 */
export const criarDistribuicaoConsumoSchema = z
  .object({
    empresaId: uuidSchema,
    depositoId: uuidSchema,
    dataLancamento: dataDoFatoSchema,
    tipoMovimentacaoEstoqueId: uuidSchema,
    observacao: z
      .string()
      .trim()
      .max(1000, 'A observação tem no máximo 1000 caracteres.')
      .prefault(''),
    linhas: z
      .array(linhaDistribuicaoConsumoSchema)
      .min(1, 'Distribua ao menos um material para uma obra.')
      .max(
        MAXIMO_LINHAS_DISTRIBUICAO,
        `A distribuição tem no máximo ${MAXIMO_LINHAS_DISTRIBUICAO} linhas.`,
      ),
  })
  .superRefine((dados, ctx) => {
    const vistas = new Set<string>();

    dados.linhas.forEach((linha, indice) => {
      const chave = chaveDaLinhaDeDistribuicao(linha);
      if (vistas.has(chave)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['linhas', indice, 'obraParteId'],
          message: MENSAGEM_LINHA_DE_DISTRIBUICAO_REPETIDA,
        });
      }
      vistas.add(chave);
    });
  });

export type CriarDistribuicaoConsumo = z.infer<typeof criarDistribuicaoConsumoSchema>;

/**
 * O que a distribuição gravou: os consumos, na ordem em que as obras apareceram.
 *
 * Os documentos inteiros, e não só os ids: a tela diz "requisições 412, 413 e
 * 414" com o valor de cada uma, e buscá-los um a um seria uma ida ao servidor
 * por obra logo depois de o servidor tê-los na mão.
 */
export const distribuicaoConsumoGravadaSchema = z.object({
  consumos: z.array(lancamentoConsumoSchema),
});

export type DistribuicaoConsumoGravada = z.infer<typeof distribuicaoConsumoGravadaSchema>;

/**
 * Uma requisição que a distribuição vai gerar, com o índice original de cada
 * linha.
 *
 * O índice viaja porque é ele que a recusa leva: o servidor valida o consumo da
 * obra, e o "item 2" daquele consumo é a "linha 7" da distribuição que o usuário
 * está vendo.
 */
export interface RequisicaoDaDistribuicao {
  obraId: string;
  consumo: CriarLancamentoConsumo;
  /** `indices[i]` é a linha da distribuição que virou o item `i` do consumo. */
  indices: number[];
}

/**
 * As linhas agrupadas por obra, já no formato do lançamento de consumo.
 *
 * No contrato porque as duas pontas precisam da mesma resposta: o servidor grava
 * um consumo por grupo, e a tela diz **antes do clique** quantas requisições
 * vão nascer. Duas implementações discordariam no dia em que a chave do grupo
 * mudasse.
 *
 * As obras saem na **ordem da primeira aparição**, e as linhas dentro de cada
 * uma na ordem em que foram digitadas: é a ordem em que os números das
 * requisições serão atribuídos, e a que o usuário espera ler.
 */
export function requisicoesDaDistribuicao(
  dados: CriarDistribuicaoConsumo,
): RequisicaoDaDistribuicao[] {
  const porObra = new Map<string, RequisicaoDaDistribuicao>();

  dados.linhas.forEach((linha, indice) => {
    let grupo = porObra.get(linha.obraId);
    if (!grupo) {
      grupo = {
        obraId: linha.obraId,
        indices: [],
        consumo: {
          empresaId: dados.empresaId,
          dataLancamento: dados.dataLancamento,
          tipoMovimentacaoEstoqueId: dados.tipoMovimentacaoEstoqueId,
          depositoId: dados.depositoId,
          obraId: linha.obraId,
          observacao: dados.observacao,
          itens: [],
        },
      };
      porObra.set(linha.obraId, grupo);
    }

    grupo.indices.push(indice);
    grupo.consumo.itens.push({
      insumoId: linha.insumoId,
      quantidade: linha.quantidade,
      obraParteId: linha.obraParteId,
      observacao: linha.observacao,
    });
  });

  return [...porObra.values()];
}

/**
 * Quanto de um material a distribuição já reparte, somando todas as obras.
 *
 * É o número que se confere contra o saldo do depósito: cada linha pode caber
 * sozinha e todas juntas não. Linhas com quantidade inválida não entram — quem
 * as recusa é o campo delas.
 */
export function totalDistribuido(
  linhas: readonly { insumoId: string; quantidade: string }[],
  insumoId: string,
): string {
  return somarQuantidade(
    linhas
      .filter((linha) => linha.insumoId === insumoId)
      .map((linha) => linha.quantidade)
      .filter((qtd) => /^\d{1,14}(\.\d{1,4})?$/.test(qtd)),
  );
}

/** A distribuição do material passa do saldo que o depósito tem? */
export function distribuicaoPassaDoSaldo(distribuido: string, saldo: string): boolean {
  return compararQuantidade(distribuido, saldo) > 0;
}
